import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Avatar, Box, useTheme } from '@mui/material';
import { type FC, useEffect, useState } from 'react';
import type { IIsPdf, MediaData } from '../../../models';
import { AvaniLightTheme } from '../../../theme/schemes/AvaniLightTheme';
import { extractVideoFrame, isVideo } from '../../../utils';
import { LightboxGallery } from './LightboxGallery';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/styles.css';

interface ImageViewerProps extends IIsPdf {
  imageSrc: MediaData[];
  index: number;
  avatarWidth?: number;
  avatarHeight?: number;
  avatarSx?: any;
}

export const ImageViewer: FC<ImageViewerProps> = ({
  imageSrc,
  index,
  avatarWidth = 100,
  avatarHeight = 100,
  avatarSx,
  isPDF = false
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();
  const [open, setOpen] = useState(false);
  const [bgUrl, setBgUrl] = useState<string | null>(null);

  // Derive a background image when we have exactly one media item that might be a video
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const currentMedia = imageSrc[index];
      const url = currentMedia?.contentUrl || '';
      if (!url) {
        setBgUrl(null);
        return;
      }
      if (isVideo(currentMedia)) {
        try {
          const thumb = await extractVideoFrame(url, 1);
          if (!cancelled) setBgUrl(thumb);
        } catch (_e) {
          // If extraction fails (e.g., CORS), clear bg so we fall back to default styling
          if (!cancelled) setBgUrl(null);
        }
      } else {
        setBgUrl(url);
      }
    })();
    return () => {
      cancelled = true;
    };
    //todo:v2-changes
  }, [imageSrc, index, isPDF]);

  const isCurrentVideo = !!imageSrc[index] && isVideo(imageSrc[index]);

  return (
    <span>
      <Box position="relative" display="inline-block">
        <Avatar
          src={bgUrl ?? ''}
          sx={{
            cursor: 'pointer',
            width: `${avatarWidth}px`,
            height: `${avatarHeight}px`,
            ...avatarSx
          }}
          imgProps={{ crossOrigin: 'anonymous' }}
          onClick={() => setOpen(true)}
        />
        {isCurrentVideo && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: Math.min(avatarWidth, avatarHeight) * 0.4 }} />
          </Box>
        )}
      </Box>

      <LightboxGallery open={open} index={index} handleClose={() => setOpen(false)} imageSrc={imageSrc} theme={theme} />
    </span>
  );
};
